import { useEffect } from 'react';

/**
 * Cursor spotlight for glass panels.
 *
 * One delegated `pointermove` listener writes `--mx` / `--my` (pointer position
 * relative to the panel) onto every `.glass` ancestor under the cursor; the CSS in
 * `@helios/ui` effects.css turns those into a soft light that follows the mouse.
 * Nothing re-renders — it is a style write inside a single animation frame — and
 * it is skipped for touch input and for users who prefer reduced motion.
 */
export function useGlassSpotlight(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let frame = 0;
    let lastEvent: PointerEvent | null = null;

    const apply = () => {
      frame = 0;
      const event = lastEvent;
      lastEvent = null;
      if (!event) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      let panel = target.closest<HTMLElement>('.glass');
      while (panel) {
        const rect = panel.getBoundingClientRect();
        panel.style.setProperty('--mx', `${event.clientX - rect.left}px`);
        panel.style.setProperty('--my', `${event.clientY - rect.top}px`);
        panel = panel.parentElement?.closest<HTMLElement>('.glass') ?? null;
      }
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      lastEvent = event;
      if (frame === 0) frame = window.requestAnimationFrame(apply);
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);
}
